import { SIGN_SANSKRIT, SIGN_ELEMENT, SIGN_GLYPH } from './signs'
import { useAstrology } from '../useDashboard'

/* ===================== HOROSCOPE ===================== */
// The planet that colours each life aspect — used to render live VedAstro
// data in the reference layout.
const PLANET_MEANING = {
  Moon: 'Emotional stability & focus', Mars: 'Energy, courage & leadership', Mercury: 'Communication & decisions',
  Venus: 'Relationships & creativity', Jupiter: 'Growth & new beginnings', Saturn: 'Discipline & responsibility',
}
// Which planet colours each life aspect (for the star ratings).
const ASPECT_PLANET = { Love: 'Venus', Career: 'Saturn', Finance: 'Jupiter', Health: 'Mars', Spirituality: 'Jupiter' }
const statusCls = (fav) =>
  fav ? 'bg-green-50 text-green-700 border-green-100' : 'bg-amber-50 text-amber-700 border-amber-100'

const ASPECTS = [
  { icon: '❤️', label: 'Love', stars: 4, note: 'Good harmony in relationships.' },
  { icon: '💼', label: 'Career', stars: 5, note: 'Excellent opportunities for growth.' },
  { icon: '💰', label: 'Finance', stars: 3, note: 'Manage expenses wisely.' },
  { icon: '🩺', label: 'Health', stars: 4, note: 'Good energy and stamina.' },
  { icon: '🪷', label: 'Spirituality', stars: 5, note: 'Your spiritual growth is favored today.' },
]

const PLANETS = [
  { icon: '🌙', name: 'Moon', where: 'in Taurus', note: 'Emotional stability & focus', status: 'Positive', cls: 'bg-green-50 text-green-700 border-green-100' },
  { icon: '♂️', name: 'Mars', where: 'in Leo', note: 'Energy, courage & leadership', status: 'Strong', cls: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  { icon: '☿', name: 'Mercury', where: 'in Cancer', note: 'Communication & decisions', status: 'Neutral', cls: 'bg-amber-50 text-amber-700 border-amber-100' },
  { icon: '♀', name: 'Venus', where: 'in Gemini', note: 'Relationships & creativity', status: 'Positive', cls: 'bg-green-50 text-green-700 border-green-100' },
  { icon: '♃', name: 'Jupiter', where: 'in Aries', note: 'Growth & new beginnings', status: 'Strong', cls: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
]

const Stars = ({ n }) => <div className="flex text-amber-400 text-[10px] mb-2">{'★'.repeat(n)}{'☆'.repeat(5 - n)}</div>

// Live calendar labels for the period cards.
function buildPeriods() {
  const d = new Date()
  const fmtD = (x) => x.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  const tomorrow = new Date(d); tomorrow.setDate(d.getDate() + 1)
  const weekEnd = new Date(d); weekEnd.setDate(d.getDate() + 6)
  return [
    { icon: '📅', title: 'Tomorrow', sub: fmtD(tomorrow) },
    { icon: '⏳', title: 'This Week', sub: `${fmtD(d)} - ${fmtD(weekEnd)}` },
    { icon: '🗓️', title: 'This Month', sub: d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) },
    { icon: '🌟', title: 'This Year', sub: String(d.getFullYear()) },
  ]
}

export default function HoroscopeTab({ data } = {}) {
  const astro = useAstrology()
  const moonSign = astro?.moonSign || 'Aries'
  const glyph = SIGN_GLYPH[moonSign] || '♈'
  const sanskrit = SIGN_SANSKRIT[moonSign] || 'मेष राशि'
  const horo = astro?.hasHoroscope ? astro.horoscope : null
  const energy = horo?.energy ?? 78
  const reading = horo?.text
    || 'A productive day for planning and taking practical steps. Your confidence will help you overcome challenges.'
  const nakshatra = astro?.kundli?.nakshatra || null
  const pan = astro?.panchang
  const paksha = pan?.tithi?.Paksha
  const todayLabel = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', weekday: 'long' })

  const luck = [
    { icon: '🎯', label: 'Lucky No', value: String(horo?.luckyNum ?? '6'), tone: 'text-stone-800' },
    { icon: '🎨', label: 'Lucky Color', value: horo?.luckyColor ?? 'Gold', tone: 'text-amber-700' },
    { icon: '🧭', label: 'Direction', value: pan?.dishaShool ?? 'West', tone: 'text-stone-800' },
    { icon: '⏰', label: 'Lucky Time', value: '10:30 - 12:00', tone: 'text-stone-800' },
    { icon: '🌙', label: 'Moon Phase', value: paksha === 'Krishna' ? 'Waning Moon' : 'Waxing Moon', tone: 'text-stone-800' },
    { icon: '🔥', label: 'Element', value: SIGN_ELEMENT[moonSign] ?? 'Fire', tone: 'text-stone-800' },
  ]

  const planets = astro?.planets
    ? astro.planets.map((p) => ({
        icon: p.icon, name: p.name, where: `in ${p.sign}`,
        note: PLANET_MEANING[p.name] || p.note, status: p.status || 'Neutral',
        cls: statusCls(p.favourable),
      }))
    : PLANETS

  const favByPlanet = {}
  astro?.planets?.forEach((p) => { favByPlanet[p.name] = p.favourable })
  const aspects = ASPECTS.map((a) => {
    const fav = favByPlanet[ASPECT_PLANET[a.label]]
    return { ...a, stars: fav == null ? a.stars : fav ? 5 : 3 }
  })

  const periods = buildPeriods()

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-6 border-b border-stone-100">
        <div className="flex flex-col">
          <h2 className="text-3xl font-extrabold text-[#3D0A0A] tracking-tight font-royal">Today's Horoscope</h2>
          <div className="text-sm text-stone-500 mt-1 font-medium">
            <span className="hover:text-[#3D0A0A] cursor-pointer transition-colors">Dashboard</span>
            <span className="mx-2 text-stone-300">›</span>
            <span className="text-[#3D0A0A] font-semibold">Horoscope</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2.5 bg-white border border-stone-200 rounded-xl shadow-sm text-sm font-semibold text-stone-700 hover:bg-stone-50 transition-all">
            <span>{todayLabel}</span>
            <svg className="w-4 h-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
          </button>
          <div className="flex gap-2">
            <button className="w-10 h-10 flex items-center justify-center rounded-xl border border-stone-200 text-[#3D0A0A] hover:bg-stone-100 transition-all active:scale-95"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg></button>
            <button className="w-10 h-10 flex items-center justify-center rounded-xl border border-stone-200 text-[#3D0A0A] hover:bg-stone-100 transition-all active:scale-95"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg></button>
          </div>
        </div>
      </div>

      <div className="mx-auto space-y-6">
        {/* Top row: sign + luck stats */}
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
          <div className="lg:col-span-3 bg-white p-4 sm:p-6 border border-stone-100 shadow-sm">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 rounded-full shrink-0 border-2 border-red-900 shadow-lg flex items-center justify-center text-5xl text-[#7A0A0A] bg-amber-50/50">{glyph}</div>
              <div className="h-24 w-px bg-stone-200" />
              <div className="flex-1 space-y-2">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-bold text-stone-900">{moonSign} <span className="text-sm font-normal text-stone-400">{sanskrit}</span></h2>
                    <p className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full inline-block mt-1">{nakshatra ? `Nakshatra · ${nakshatra}` : 'Chandra Rashi'}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider block">Today's Energy</span>
                    <span className="text-xl font-black text-[#3D0A0A]">{energy}%</span>
                  </div>
                </div>
                <div className="h-1.5 bg-stone-100 rounded-full w-full overflow-hidden"><div className="h-full bg-amber-500 rounded-full" style={{ width: `${energy}%` }} /></div>
                <p className="text-xs text-stone-600 leading-relaxed pt-1">{reading}</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-3 bg-white p-5 border border-stone-100 shadow-sm">
            <div className="grid grid-cols-2 gap-y-3 gap-x-2">
              {luck.map((l) => (
                <div key={l.label} className="flex items-center gap-3 border border-stone-100 rounded-xl p-2">
                  <div className="w-8 h-8 rounded-full bg-stone-50 flex items-center justify-center text-red-900 shrink-0">{l.icon}</div>
                  <div>
                    <p className="text-[9px] uppercase tracking-wider text-stone-400 font-bold">{l.label}</p>
                    <p className={`text-xs font-bold ${l.tone}`}>{l.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Aspects + planetary influences */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start w-full">
          <div className="bg-white p-4 xl:col-span-7 border border-stone-100 shadow-sm w-full">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
              {aspects.map((a) => (
                <div key={a.label} className="flex flex-col items-center text-center p-2 hover:bg-stone-50 rounded-xl transition-colors">
                  <span className="text-3xl mb-3">{a.icon}</span>
                  <span className="font-bold text-stone-800 text-sm mb-1">{a.label}</span>
                  <Stars n={a.stars} />
                  <p className="text-[11px] text-stone-500 leading-tight px-1">{a.note}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-stone-100 my-6" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="flex items-start gap-4 p-4">
                <div className="text-2xl text-amber-600 mt-1">✦</div>
                <div>
                  <h3 className="font-bold text-stone-900 text-sm mb-2">Today's Guidance</h3>
                  <p className="text-xs text-stone-600 leading-relaxed mb-3">Stay focused on your priorities. Avoid unnecessary distractions and trust your inner wisdom. Your consistent efforts will bring fruitful results.</p>
                  <p className="text-[11px] font-bold text-stone-800">— Astrologer Raju Acharya</p>
                </div>
              </div>
              <div className="flex flex-col gap-4 bg-stone-50 p-4 border border-stone-100 items-center text-center rounded-xl">
                <div className="flex flex-col items-center">
                  <div className="text-3xl mb-2">🌺</div>
                  <h3 className="font-bold text-stone-900 text-sm mb-1">Remedy of the Day</h3>
                  <p className="text-xs text-stone-600 mb-3 leading-relaxed">Offer red flowers to Lord Hanuman and chant the Hanuman Chalisa.</p>
                  <button className="text-[10px] font-bold border border-amber-600 text-amber-700 px-4 py-1.5 rounded-full hover:bg-amber-600 hover:text-white transition-all">VIEW ALL REMEDIES</button>
                </div>
                <div className="w-20 h-20 shrink-0 rounded-full border-2 border-white shadow-md bg-amber-100 flex items-center justify-center text-3xl">🙏</div>
              </div>
            </div>
          </div>

          <div className="xl:col-span-5 w-full">
            <div className="bg-white p-4 sm:p-6 border border-stone-100 shadow-[0_2px_15px_rgba(0,0,0,0.03)]">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-stone-800 flex items-center gap-2">Planetary Influences</h2>
                <span className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Status</span>
              </div>
              <div className="space-y-4">
                {planets.map((p, i) => (
                  <div key={p.name}>
                    <div className="grid grid-cols-[40px_1fr_auto] gap-4 items-center group">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-stone-50 text-xl group-hover:bg-amber-50 transition-colors">{p.icon}</div>
                      <div>
                        <p className="font-bold text-stone-800 text-sm">{p.name} <span className="font-normal text-stone-400 ml-1">{p.where}</span></p>
                        <p className="text-xs text-stone-500">{p.note}</p>
                      </div>
                      <span className={`text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider border ${p.cls}`}>{p.status}</span>
                    </div>
                    {i < PLANETS.length - 1 && <div className="h-px bg-stone-100 mt-4" />}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center py-10">
              <a href="#" className="inline-flex items-center justify-center gap-3 bg-[#6b1626] hover:bg-[#52111d] text-white font-medium py-4 px-8 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg w-full max-w-sm">
                <span className="tracking-wider uppercase text-sm font-semibold">Read Complete Horoscope</span>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </a>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
          {periods.map((p) => (
            <div key={p.title} className="bg-white p-4 border border-stone-100 shadow-sm flex items-center gap-3 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-stone-50 text-red-900 shrink-0">{p.icon}</div>
              <div>
                <p className="text-xs font-bold text-stone-800">{p.title}</p>
                <p className="text-[10px] text-stone-500">{p.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
